/**
 * Workflow Formula UI helpers.
 * Variable chips come only from GET conductor/approval-workflows/variables?workflow_type=...
 * (server merges COMMON + transaction catalog rows — no local variable hardcode).
 */

export const APPROVAL_ACTIONS = ['APPROVE', 'REJECT'];
export const VIEW_ACTIONS = ['NEXT'];
export const ACTION_OPTIONS = [...APPROVAL_ACTIONS, ...VIEW_ACTIONS];

export const WORKFLOW_TYPE_OPTIONS = [
  {
    value: 'AP_INVOICE',
    label: 'Payment Intelligence',
    helper: 'Applies to Payment Intelligence approval (AP_INVOICE)',
    enabled: true,
  },
  {
    value: 'WORKFORCE_SCHEDULE',
    label: 'Workforce Schedule',
    helper: 'Not wired yet',
    enabled: false,
  },
  {
    value: 'PAYMENT_BATCH',
    label: 'Payment Batch',
    helper: 'Not wired yet',
    enabled: false,
  },
];

export const CONDITION_OPS = [
  { value: 'GT', label: '>' },
  { value: 'GTE', label: '≥' },
  { value: 'LT', label: '<' },
  { value: 'LTE', label: '≤' },
  { value: 'EQ', label: '=' },
  { value: 'NEQ', label: '≠' },
  { value: 'CONTAINS', label: 'contains' },
  { value: 'IS_NULL', label: 'is empty' },
  { value: 'IS_NOT_NULL', label: 'is not empty' },
];

/** Map API catalog response variables into UI chip shape. */
export const mapCatalogVariables = (apiVariables) => {
  if (!Array.isArray(apiVariables)) return [];
  return apiVariables
    .filter((v) => v && v.key)
    .map((v) => ({
      key: v.key,
      label: v.label || v.key,
      group: v.group || (v.category === 'COMMON' ? 'Common' : 'Invoice'),
      type: v.dataType || v.type || 'string',
      aliases: Array.isArray(v.aliases) ? v.aliases : [],
      jsonPath: v.jsonPath || `$.${v.key}`,
      description: v.description || '',
    }));
};

export const emptyConditionClause = () => ({ field: '', op: 'GTE', value: '' });

const parseClauseNode = (node) => {
  if (!node || typeof node !== 'object') return emptyConditionClause();
  return {
    field: node.field ? String(node.field) : '',
    op: node.op ? String(node.op).toUpperCase() : 'GTE',
    value: node.value != null ? String(node.value) : '',
  };
};

/** Parse simple or combined {"and":[...]} / {"or":[...]} JSON into builder state. */
export const parseConditionBuilder = (expression) => {
  if (!expression || !String(expression).trim()) {
    return { join: 'AND', clauses: [emptyConditionClause()] };
  }
  try {
    const node = JSON.parse(String(expression).trim());
    if (node && typeof node === 'object' && Array.isArray(node.and) && node.and.length) {
      return { join: 'AND', clauses: node.and.map(parseClauseNode) };
    }
    if (node && typeof node === 'object' && Array.isArray(node.or) && node.or.length) {
      return { join: 'OR', clauses: node.or.map(parseClauseNode) };
    }
    if (node && typeof node === 'object' && node.field && node.op) {
      return { join: 'AND', clauses: [parseClauseNode(node)] };
    }
  } catch {
    /* keep empty builder; raw JSON still shown */
  }
  return { join: 'AND', clauses: [emptyConditionClause()], rawOnly: true };
};

export const buildClauseJson = ({ field, op, value }) => {
  if (!field || !op) return null;
  const payload = { field, op };
  if (op !== 'IS_NULL' && op !== 'IS_NOT_NULL') {
    payload.value = value ?? '';
  }
  return payload;
};

export const buildConditionJson = ({ join, clauses }) => {
  const built = (clauses || []).map(buildClauseJson).filter(Boolean);
  if (!built.length) return '';
  if (built.length === 1) return JSON.stringify(built[0]);
  const key = String(join || 'AND').toUpperCase() === 'OR' ? 'or' : 'and';
  return JSON.stringify({ [key]: built });
};

/** Parse simple {"field","op","value"} JSON into builder state. */
export const parseSimpleCondition = (expression) => {
  const parsed = parseConditionBuilder(expression);
  return parsed.clauses[0] || emptyConditionClause();
};

export const buildSimpleConditionJson = ({ field, op, value }) => (
  buildConditionJson({ join: 'AND', clauses: [{ field, op, value }] })
);
